import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/* ═══════════════════════════════════════════════════════════════
   usePartograph — Custom hook for Digital Partograph
   Manages partograph state, entries, chart calculations,
   alert/action line logic, and danger sign detection.
   ═══════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════
   MOCK DATA — Set to true to bypass API and render demo data.
   ════════════════════════════════════════════════════════════════ */
const USE_MOCK_DATA = false;

const MOCK_START_TIME = (function() {
    const d = new Date();
    d.setHours(d.getHours() - 9);
    d.setMinutes(0, 0, 0);
    return d;
})();

const mockTimeAt = function(hoursAfterStart) {
    const d = new Date(MOCK_START_TIME.getTime());
    d.setMinutes(d.getMinutes() + Math.round(hoursAfterStart * 60));
    const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

const MOCK_ADMISSION = {
    id: 9001,
    patient_number: "MOCK-PT-001",
    visit_id: 5001,
    booking_id: 3001,
    admission_datetime: mockTimeAt(-1),
    admitted_by: "Dr. Sarah Nakamya",
    referral_source: "self",
    gestational_age_weeks: 39,
    onset_type: "spontaneous",
    membrane_status: "intact",
    initial_cervical_dilation_cm: 4,
    initial_fetal_hr: 140,
    initial_bp_systolic: 118,
    initial_bp_diastolic: 72,
    initial_pulse: 82,
    initial_temperature: 36.8,
    fetal_presentation: "cephalic",
    fetal_position: "LOA",
    fetal_lie: "longitudinal",
    status: "in_labour",
};

const MOCK_PARTOGRAPH = {
    id: 7001,
    patient_admission_id: 9001,
    started_at: mockTimeAt(0),
    status: "active",
    notes: "Active labour established. Cervix 4cm dilated, fully effaced. Contractions 2 in 10 min. Membranes intact.",
};

const MOCK_ENTRIES = [
    {
        id: 8001,
        partograph_id: 7001,
        recorded_at: mockTimeAt(0),
        hours_since_start: 0,
        cervical_dilation_cm: 4,
        descent_fifths: 4,
        fetal_heart_rate: 140,
        amniotic_fluid: "clear",
        moulding: "0",
        contractions_per_10min: 2,
        contraction_duration_seconds: 20,
        maternal_pulse: 82,
        maternal_bp_systolic: 118,
        maternal_bp_diastolic: 72,
        maternal_temperature: 36.8,
        urine_volume_ml: 200,
        urine_protein: "nil",
        urine_acetone: "nil",
        oxytocin_drops_per_min: "",
        iv_fluids: "",
        drugs_given: "",
    },
    {
        id: 8002,
        partograph_id: 7001,
        recorded_at: mockTimeAt(1),
        hours_since_start: 1,
        cervical_dilation_cm: 4.5,
        descent_fifths: 4,
        fetal_heart_rate: 138,
        amniotic_fluid: "clear",
        moulding: "0",
        contractions_per_10min: 2,
        contraction_duration_seconds: 25,
        maternal_pulse: 80,
        maternal_bp_systolic: 120,
        maternal_bp_diastolic: 74,
        maternal_temperature: 36.7,
        urine_volume_ml: "",
        urine_protein: "",
        urine_acetone: "",
        oxytocin_drops_per_min: "",
        iv_fluids: "Ringer's Lactate 500ml",
        drugs_given: "",
    },
    {
        id: 8003,
        partograph_id: 7001,
        recorded_at: mockTimeAt(2),
        hours_since_start: 2,
        cervical_dilation_cm: 5,
        descent_fifths: 3,
        fetal_heart_rate: 144,
        amniotic_fluid: "clear",
        moulding: "0",
        contractions_per_10min: 3,
        contraction_duration_seconds: 30,
        maternal_pulse: 84,
        maternal_bp_systolic: 122,
        maternal_bp_diastolic: 76,
        maternal_temperature: 36.9,
        urine_volume_ml: 150,
        urine_protein: "nil",
        urine_acetone: "nil",
        oxytocin_drops_per_min: "",
        iv_fluids: "Ringer's Lactate 500ml",
        drugs_given: "",
    },
    {
        id: 8004,
        partograph_id: 7001,
        recorded_at: mockTimeAt(3),
        hours_since_start: 3,
        cervical_dilation_cm: 5.5,
        descent_fifths: 3,
        fetal_heart_rate: 148,
        amniotic_fluid: "clear",
        moulding: "1",
        contractions_per_10min: 3,
        contraction_duration_seconds: 35,
        maternal_pulse: 86,
        maternal_bp_systolic: 126,
        maternal_bp_diastolic: 78,
        maternal_temperature: 37.0,
        urine_volume_ml: "",
        urine_protein: "",
        urine_acetone: "",
        oxytocin_drops_per_min: "",
        iv_fluids: "",
        drugs_given: "",
    },
    {
        id: 8005,
        partograph_id: 7001,
        recorded_at: mockTimeAt(4),
        hours_since_start: 4,
        cervical_dilation_cm: 6,
        descent_fifths: 3,
        fetal_heart_rate: 136,
        amniotic_fluid: "clear",
        moulding: "1",
        contractions_per_10min: 3,
        contraction_duration_seconds: 40,
        maternal_pulse: 88,
        maternal_bp_systolic: 130,
        maternal_bp_diastolic: 82,
        maternal_temperature: 37.1,
        urine_volume_ml: 100,
        urine_protein: "trace",
        urine_acetone: "nil",
        oxytocin_drops_per_min: "",
        iv_fluids: "Ringer's Lactate 500ml",
        drugs_given: "",
    },
    {
        id: 8006,
        partograph_id: 7001,
        recorded_at: mockTimeAt(5),
        hours_since_start: 5,
        cervical_dilation_cm: 6.5,
        descent_fifths: 2,
        fetal_heart_rate: 152,
        amniotic_fluid: "clear",
        moulding: "1",
        contractions_per_10min: 4,
        contraction_duration_seconds: 40,
        maternal_pulse: 90,
        maternal_bp_systolic: 128,
        maternal_bp_diastolic: 80,
        maternal_temperature: 37.2,
        urine_volume_ml: "",
        urine_protein: "",
        urine_acetone: "",
        oxytocin_drops_per_min: "",
        iv_fluids: "",
        drugs_given: "Paracetamol 1g PO",
    },
    {
        id: 8007,
        partograph_id: 7001,
        recorded_at: mockTimeAt(6),
        hours_since_start: 6,
        cervical_dilation_cm: 7,
        descent_fifths: 2,
        fetal_heart_rate: 162,
        amniotic_fluid: "meconium_1",
        moulding: "1",
        contractions_per_10min: 4,
        contraction_duration_seconds: 45,
        maternal_pulse: 92,
        maternal_bp_systolic: 134,
        maternal_bp_diastolic: 84,
        maternal_temperature: 37.3,
        urine_volume_ml: 80,
        urine_protein: "trace",
        urine_acetone: "trace",
        oxytocin_drops_per_min: "",
        iv_fluids: "Normal Saline 500ml",
        drugs_given: "",
    },
    {
        id: 8008,
        partograph_id: 7001,
        recorded_at: mockTimeAt(7),
        hours_since_start: 7,
        cervical_dilation_cm: 7.5,
        descent_fifths: 1,
        fetal_heart_rate: 156,
        amniotic_fluid: "meconium_1",
        moulding: "2",
        contractions_per_10min: 4,
        contraction_duration_seconds: 50,
        maternal_pulse: 94,
        maternal_bp_systolic: 142,
        maternal_bp_diastolic: 92,
        maternal_temperature: 37.4,
        urine_volume_ml: "",
        urine_protein: "+",
        urine_acetone: "trace",
        oxytocin_drops_per_min: "",
        iv_fluids: "Normal Saline 500ml",
        drugs_given: "",
    },
    {
        id: 8009,
        partograph_id: 7001,
        recorded_at: mockTimeAt(8),
        hours_since_start: 8,
        cervical_dilation_cm: 8,
        descent_fifths: 1,
        fetal_heart_rate: 148,
        amniotic_fluid: "meconium_2",
        moulding: "2",
        contractions_per_10min: 5,
        contraction_duration_seconds: 55,
        maternal_pulse: 96,
        maternal_bp_systolic: 138,
        maternal_bp_diastolic: 88,
        maternal_temperature: 37.6,
        urine_volume_ml: 60,
        urine_protein: "+",
        urine_acetone: "+",
        oxytocin_drops_per_min: 8,
        iv_fluids: "Normal Saline 500ml + Oxytocin 5IU",
        drugs_given: "Ampicillin 2g IV",
    },
    {
        id: 8010,
        partograph_id: 7001,
        recorded_at: mockTimeAt(8.5),
        hours_since_start: 8.5,
        cervical_dilation_cm: 9,
        descent_fifths: 1,
        fetal_heart_rate: 144,
        amniotic_fluid: "meconium_2",
        moulding: "2",
        contractions_per_10min: 5,
        contraction_duration_seconds: 60,
        maternal_pulse: 98,
        maternal_bp_systolic: 136,
        maternal_bp_diastolic: 86,
        maternal_temperature: 37.5,
        urine_volume_ml: "",
        urine_protein: "",
        urine_acetone: "",
        oxytocin_drops_per_min: 10,
        iv_fluids: "Normal Saline 500ml + Oxytocin 5IU",
        drugs_given: "",
    },
];

/* ─── Constants ─── */

const AMNIOTIC_FLUID = [
    { value: "clear", label: "C — Clear" },
    { value: "meconium_1", label: "M1 — Meconium Grade I (light)" },
    { value: "meconium_2", label: "M2 — Meconium Grade II (moderate)" },
    { value: "meconium_3", label: "M3 — Meconium Grade III (thick)" },
    { value: "blood", label: "B — Blood-stained" },
    { value: "absent", label: "A — Absent (dry)" },
];

const MOULDING_OPTIONS = [
    { value: "0", label: "0 — No moulding" },
    { value: "1", label: "+ — Bones touching" },
    { value: "2", label: "++ — Bones overlapping but reducible" },
    { value: "3", label: "+++ — Bones overlapping, irreducible" },
];

const URINE_PROTEIN_OPTIONS = [
    { value: "nil", label: "Nil" },
    { value: "trace", label: "Trace" },
    { value: "+", label: "+" },
    { value: "++", label: "++" },
    { value: "+++", label: "+++" },
];

const URINE_ACETONE_OPTIONS = [
    { value: "nil", label: "Nil" },
    { value: "trace", label: "Trace" },
    { value: "+", label: "+" },
    { value: "++", label: "++" },
    { value: "+++", label: "+++" },
];

const PARTOGRAPH_STATUS = [
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "abandoned", label: "Abandoned" },
];

/* ─── Helper: format datetime-local ─── */
const nowLocal = function() {
    const d = new Date();
    const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

/* ─── WHO Alert & Action Line Computation ─── */
const getAlertLineDilation = function(hoursSinceStart) {
    const dilation = 4 + hoursSinceStart;
    return dilation > 10 ? 10 : dilation;
};

const getActionLineDilation = function(hoursSinceStart) {
    const adjusted = hoursSinceStart - 4;
    if (adjusted < 0) return null;
    const dilation = 4 + adjusted;
    return dilation > 10 ? 10 : dilation;
};

/* ─── Initial Entry Form State ─── */
const INITIAL_ENTRY = {
    recorded_at: nowLocal(),
    cervical_dilation_cm: "",
    descent_fifths: "",
    contractions_per_10min: "",
    contraction_duration_seconds: "",
    fetal_heart_rate: "",
    amniotic_fluid: "",
    moulding: "",
    maternal_pulse: "",
    maternal_bp_systolic: "",
    maternal_bp_diastolic: "",
    maternal_temperature: "",
    urine_volume_ml: "",
    urine_protein: "",
    urine_acetone: "",
    oxytocin_drops_per_min: "",
    iv_fluids: "",
    drugs_given: "",
};

/* ═══ HOOK ═══ */
export const usePartograph = function(actions, patient_number, visit_id) {

    /* ─── Admission data ─── */
    const [admissionData, setAdmissionData] = useState(null);
    const [loadingAdmission, setLoadingAdmission] = useState(true);

    /* ─── Partograph record ─── */
    const [partograph, setPartograph] = useState(null);

    /* ─── All entries ─── */
    const [entries, setEntries] = useState([]);

    /* ─── Entry form (for adding new) ─── */
    const [entryForm, setEntryForm] = useState(Object.assign({}, INITIAL_ENTRY));

    /* ─── Editing entry ─── */
    const [editingEntry, setEditingEntry] = useState(null);

    /* ─── UI state ─── */
    const [activeTab, setActiveTab] = useState("entry");
    const [saving, setSaving] = useState(false);
    const [starting, setStarting] = useState(false);
    const [startForm, setStartForm] = useState({ started_at: nowLocal(), notes: "" });

    /* ─── Fetch admission record ─── */
    useEffect(function() {
        if (!patient_number || !visit_id) return;
        setLoadingAdmission(true);
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                setAdmissionData(MOCK_ADMISSION);
                setLoadingAdmission(false);
            }, 400);
            return;
        }
        axios
            .get(config.smsUrl + "/obgyn/labour/admission-by-visit/" + visit_id)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) setAdmissionData(data);
            })
            .catch(function() { setAdmissionData(null); })
            .finally(function() { setLoadingAdmission(false); });
    }, [patient_number, visit_id]);

    /* ─── Fetch partograph + entries ─── */
    useEffect(function() {
        if (!admissionData || !admissionData.id) return;
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                setPartograph(MOCK_PARTOGRAPH);
                const sorted = MOCK_ENTRIES.slice().sort(function(a, b) {
                    return (a.hours_since_start || 0) - (b.hours_since_start || 0);
                });
                setEntries(sorted);
            }, 300);
            return;
        }
        axios
            .get(config.smsUrl + "/obgyn/labour/partograph/" + admissionData.id)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) {
                    setPartograph(data);
                    const sorted = (data.entries || []).slice().sort(function(a, b) {
                        return (a.hours_since_start || 0) - (b.hours_since_start || 0);
                    });
                    setEntries(sorted);
                }
            })
            .catch(function() {
                setPartograph(null);
                setEntries([]);
            });
    }, [admissionData]);

    /* ─── Handle entry form changes ─── */
    const handleEntryChange = useCallback(function(e) {
        const name = e.target.name;
        const value = e.target.value;
        setEntryForm(function(prev) {
            return Object.assign({}, prev, { [name]: value });
        });
    }, []);

    /* ─── Handle start form changes ─── */
    const handleStartChange = useCallback(function(e) {
        const name = e.target.name;
        const value = e.target.value;
        setStartForm(function(prev) {
            return Object.assign({}, prev, { [name]: value });
        });
    }, []);

    /* ─── Compute hours since start for entry ─── */
    const computeHoursSinceStart = useCallback(function(recordedAt) {
        if (!partograph || !partograph.started_at) return 0;
        const startMs = new Date(partograph.started_at).getTime();
        const recMs = new Date(recordedAt).getTime();
        const diff = (recMs - startMs) / (1000 * 60 * 60);
        return Math.round(diff * 10) / 10;
    }, [partograph]);

    /* ─── Start partograph ─── */
    const startPartograph = useCallback(function() {
        if (!admissionData || !admissionData.id) {
            actions.snackbarActions.snackError("No admission found");
            return;
        }
        setStarting(true);
        const payload = {
            patient_admission_id: admissionData.id,
            started_at: startForm.started_at,
            notes: startForm.notes,
            status: "active",
        };
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                const mockParto = Object.assign({}, payload, { id: Date.now() });
                setPartograph(mockParto);
                setEntries([]);
                actions.snackbarActions.snackSuccess("Partograph started (mock)");
                setActiveTab("entry");
                setStarting(false);
            }, 300);
            return;
        }
        axios
            .post(config.smsUrl + "/obgyn/labour/partograph/start", payload)
            .then(function(res) {
                const data = res.data || payload;
                setPartograph(data);
                setEntries([]);
                actions.snackbarActions.snackSuccess("Partograph started");
                setActiveTab("entry");
            })
            .catch(function(err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to start partograph";
                actions.snackbarActions.snackError(msg);
            })
            .finally(function() { setStarting(false); });
    }, [admissionData, startForm, actions]);

    /* ─── Add entry ─── */
    const addEntry = useCallback(function() {
        if (!partograph || !partograph.id) {
            actions.snackbarActions.snackError("Partograph not started");
            return;
        }
        if (!entryForm.cervical_dilation_cm && !entryForm.fetal_heart_rate) {
            actions.snackbarActions.snackError("Enter at least dilation or FHR");
            return;
        }
        setSaving(true);
        const hours = computeHoursSinceStart(entryForm.recorded_at);
        const payload = Object.assign({}, entryForm, {
            partograph_id: partograph.id,
            hours_since_start: hours,
        });
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                const newEntry = Object.assign({}, payload, { id: Date.now() });
                newEntry.hours_since_start = hours;
                const updated = entries.concat([newEntry]).sort(function(a, b) {
                    return (a.hours_since_start || 0) - (b.hours_since_start || 0);
                });
                setEntries(updated);
                setEntryForm(Object.assign({}, INITIAL_ENTRY, { recorded_at: nowLocal() }));
                actions.snackbarActions.snackSuccess("Entry recorded (mock)");
                setActiveTab("entries");
                setSaving(false);
            }, 200);
            return;
        }
        axios
            .post(config.smsUrl + "/obgyn/labour/partograph/entry", payload)
            .then(function(res) {
                const newEntry = res.data || Object.assign({}, payload, { id: Date.now() });
                newEntry.hours_since_start = hours;
                const updated = entries.concat([newEntry]).sort(function(a, b) {
                    return (a.hours_since_start || 0) - (b.hours_since_start || 0);
                });
                setEntries(updated);
                setEntryForm(Object.assign({}, INITIAL_ENTRY, { recorded_at: nowLocal() }));
                actions.snackbarActions.snackSuccess("Entry recorded");
                setActiveTab("entries");
            })
            .catch(function(err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to save entry";
                actions.snackbarActions.snackError(msg);
            })
            .finally(function() { setSaving(false); });
    }, [partograph, entryForm, entries, computeHoursSinceStart, actions]);

    /* ─── Edit entry (load into form) ─── */
    const startEditEntry = useCallback(function(entry) {
        setEditingEntry(entry);
        setEntryForm({
            recorded_at: entry.recorded_at || nowLocal(),
            cervical_dilation_cm: entry.cervical_dilation_cm || "",
            descent_fifths: entry.descent_fifths || "",
            contractions_per_10min: entry.contractions_per_10min || "",
            contraction_duration_seconds: entry.contraction_duration_seconds || "",
            fetal_heart_rate: entry.fetal_heart_rate || "",
            amniotic_fluid: entry.amniotic_fluid || "",
            moulding: entry.moulding || "",
            maternal_pulse: entry.maternal_pulse || "",
            maternal_bp_systolic: entry.maternal_bp_systolic || "",
            maternal_bp_diastolic: entry.maternal_bp_diastolic || "",
            maternal_temperature: entry.maternal_temperature || "",
            urine_volume_ml: entry.urine_volume_ml || "",
            urine_protein: entry.urine_protein || "",
            urine_acetone: entry.urine_acetone || "",
            oxytocin_drops_per_min: entry.oxytocin_drops_per_min || "",
            iv_fluids: entry.iv_fluids || "",
            drugs_given: entry.drugs_given || "",
        });
        setActiveTab("entry");
    }, []);

    /* ─── Save edited entry ─── */
    const updateEntry = useCallback(function() {
        if (!editingEntry || !editingEntry.id) return;
        setSaving(true);
        const hours = computeHoursSinceStart(entryForm.recorded_at);
        const payload = Object.assign({}, entryForm, { hours_since_start: hours });
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                const updated = entries.map(function(e) {
                    if (e.id === editingEntry.id) {
                        return Object.assign({}, e, payload);
                    }
                    return e;
                }).sort(function(a, b) {
                    return (a.hours_since_start || 0) - (b.hours_since_start || 0);
                });
                setEntries(updated);
                setEditingEntry(null);
                setEntryForm(Object.assign({}, INITIAL_ENTRY, { recorded_at: nowLocal() }));
                actions.snackbarActions.snackSuccess("Entry updated (mock)");
                setActiveTab("entries");
                setSaving(false);
            }, 200);
            return;
        }
        axios
            .put(config.smsUrl + "/obgyn/labour/partograph/entry/" + editingEntry.id, payload)
            .then(function() {
                const updated = entries.map(function(e) {
                    if (e.id === editingEntry.id) {
                        return Object.assign({}, e, payload);
                    }
                    return e;
                }).sort(function(a, b) {
                    return (a.hours_since_start || 0) - (b.hours_since_start || 0);
                });
                setEntries(updated);
                setEditingEntry(null);
                setEntryForm(Object.assign({}, INITIAL_ENTRY, { recorded_at: nowLocal() }));
                actions.snackbarActions.snackSuccess("Entry updated");
                setActiveTab("entries");
            })
            .catch(function(err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to update entry";
                actions.snackbarActions.snackError(msg);
            })
            .finally(function() { setSaving(false); });
    }, [editingEntry, entryForm, entries, computeHoursSinceStart, actions]);

    /* ─── Cancel edit ─── */
    const cancelEdit = useCallback(function() {
        setEditingEntry(null);
        setEntryForm(Object.assign({}, INITIAL_ENTRY, { recorded_at: nowLocal() }));
    }, []);

    /* ─── Complete partograph ─── */
    const completePartograph = useCallback(function() {
        if (!partograph || !partograph.id) return;
        if (USE_MOCK_DATA) {
            setPartograph(Object.assign({}, partograph, { status: "completed" }));
            setActiveTab("entries");
            actions.snackbarActions.snackSuccess("Partograph completed (mock)");
            return;
        }
        axios
            .put(config.smsUrl + "/obgyn/labour/partograph/" + partograph.id, { status: "completed" })
            .then(function() {
                setPartograph(Object.assign({}, partograph, { status: "completed" }));
                setActiveTab("entries");
                actions.snackbarActions.snackSuccess("Partograph completed");
            })
            .catch(function() {
                actions.snackbarActions.snackError("Failed to complete partograph");
            });
    }, [partograph, actions]);

    /* ═══ CHART DATA COMPUTATION ═══ */

    /* ── Cervical dilation data points ── */
    const dilationData = useMemo(function() {
        return entries
            .filter(function(e) { return e.cervical_dilation_cm != null && e.cervical_dilation_cm !== ""; })
            .map(function(e) {
                return {
                    hour: parseFloat(e.hours_since_start) || 0,
                    value: parseFloat(e.cervical_dilation_cm),
                };
            });
    }, [entries]);

    /* ── Descent data points ── */
    const descentData = useMemo(function() {
        return entries
            .filter(function(e) { return e.descent_fifths != null && e.descent_fifths !== ""; })
            .map(function(e) {
                return {
                    hour: parseFloat(e.hours_since_start) || 0,
                    value: parseFloat(e.descent_fifths),
                };
            });
    }, [entries]);

    /* ── FHR data points ── */
    const fhrData = useMemo(function() {
        return entries
            .filter(function(e) { return e.fetal_heart_rate != null && e.fetal_heart_rate !== ""; })
            .map(function(e) {
                return {
                    hour: parseFloat(e.hours_since_start) || 0,
                    value: parseFloat(e.fetal_heart_rate),
                };
            });
    }, [entries]);

    /* ── Contraction data ── */
    const contractionData = useMemo(function() {
        return entries
            .filter(function(e) { return e.contractions_per_10min != null && e.contractions_per_10min !== ""; })
            .map(function(e) {
                return {
                    hour: parseFloat(e.hours_since_start) || 0,
                    count: parseFloat(e.contractions_per_10min),
                    duration: parseFloat(e.contraction_duration_seconds) || 0,
                };
            });
    }, [entries]);

    /* ── Maternal vitals data ── */
    const maternalData = useMemo(function() {
        return entries
            .filter(function(e) {
                return e.maternal_pulse || e.maternal_bp_systolic || e.maternal_temperature;
            })
            .map(function(e) {
                return {
                    hour: parseFloat(e.hours_since_start) || 0,
                    pulse: parseFloat(e.maternal_pulse) || null,
                    bpSystolic: parseFloat(e.maternal_bp_systolic) || null,
                    bpDiastolic: parseFloat(e.maternal_bp_diastolic) || null,
                    temperature: parseFloat(e.maternal_temperature) || null,
                };
            });
    }, [entries]);

    /* ── Alert/Action line data (12 hours) ── */
    const alertLineData = useMemo(function() {
        const points = [];
        for (let h = 0; h <= 12; h++) {
            const d = getAlertLineDilation(h);
            if (d <= 10) points.push({ hour: h, value: d });
        }
        return points;
    }, []);

    const actionLineData = useMemo(function() {
        const points = [];
        for (let h = 4; h <= 16; h++) {
            const d = getActionLineDilation(h);
            if (d !== null && d <= 10) points.push({ hour: h, value: d });
        }
        return points;
    }, []);

    /* ── Max hour on chart ── */
    const maxHour = useMemo(function() {
        let max = 12;
        entries.forEach(function(e) {
            const h = parseFloat(e.hours_since_start) || 0;
            if (h > max) max = h;
        });
        return Math.ceil(max) + 1;
    }, [entries]);

    /* ═══ ALERTS ═══ */
    const alerts = useMemo(function() {
        const list = [];
        if (!entries.length) return list;

        const latest = entries[entries.length - 1];
        const latestHour = parseFloat(latest.hours_since_start) || 0;

        /* Alert line crossed */
        const latestDilation = parseFloat(latest.cervical_dilation_cm);
        if (latestDilation && latestHour > 0) {
            const alertDilation = getAlertLineDilation(latestHour);
            if (latestDilation < alertDilation) {
                list.push({
                    type: "warning",
                    icon: "⚠️",
                    title: "Alert Line Crossed",
                    detail: "Cervical dilation (" + latestDilation + "cm) is behind the alert line (" + alertDilation + "cm) at " + latestHour + " hours.",
                    action: "Assess progress. Prepare for possible intervention.",
                });
            }
            const actionDilation = getActionLineDilation(latestHour);
            if (actionDilation !== null && latestDilation < actionDilation) {
                list.push({
                    type: "critical",
                    icon: "🚨",
                    title: "Action Line Crossed",
                    detail: "Cervical dilation (" + latestDilation + "cm) is behind the action line (" + actionDilation + "cm). Prolonged labour!",
                    action: "URGENT: Evaluate for C-section, augmentation, or referral.",
                });
            }
        }

        /* Abnormal FHR */
        const fhr = parseFloat(latest.fetal_heart_rate);
        if (fhr && (fhr < 110 || fhr > 160)) {
            list.push({
                type: "critical",
                icon: "💗",
                title: fhr < 110 ? "Fetal Bradycardia" : "Fetal Tachycardia",
                detail: "FHR " + fhr + " bpm at " + latestHour + "h — " +
                    (fhr < 100 ? "Severe bradycardia" : fhr < 110 ? "Bradycardia" : fhr > 180 ? "Severe tachycardia" : "Tachycardia"),
                action: fhr < 100 || fhr > 180
                    ? "URGENT: Immediate assessment. Consider emergency delivery."
                    : "Close monitoring. Position change. Assess cause.",
            });
        }

        /* Meconium */
        const fluid = latest.amniotic_fluid;
        if (fluid && fluid.indexOf("meconium") >= 0) {
            const grade = fluid === "meconium_3" ? "critical" : "warning";
            list.push({
                type: grade,
                icon: "💧",
                title: "Meconium-stained Liquor",
                detail: "Amniotic fluid: " + (fluid === "meconium_1" ? "Grade I (light)" :
                    fluid === "meconium_2" ? "Grade II (moderate)" : "Grade III (thick)"),
                action: fluid === "meconium_3"
                    ? "URGENT: Prepare for neonatal resuscitation. Consider delivery."
                    : "Monitor FHR closely. Prepare resuscitation equipment.",
            });
        }

        /* Significant moulding */
        const mould = parseInt(latest.moulding);
        if (!isNaN(mould) && mould >= 2) {
            list.push({
                type: mould >= 3 ? "critical" : "warning",
                icon: "🦴",
                title: "Significant Moulding",
                detail: "Moulding " + (mould === 2 ? "++" : "+++") + " — " +
                    (mould >= 3 ? "Irreducible overlapping (cephalopelvic disproportion likely)" : "Overlapping but reducible"),
                action: mould >= 3
                    ? "URGENT: Assess for obstructed labour. Consider C-section."
                    : "Monitor progress closely. Assess descent.",
            });
        }

        /* Maternal hypertension */
        const bpSys = parseFloat(latest.maternal_bp_systolic);
        const bpDia = parseFloat(latest.maternal_bp_diastolic);
        if ((!isNaN(bpSys) && bpSys >= 140) || (!isNaN(bpDia) && bpDia >= 90)) {
            list.push({
                type: (bpSys >= 160 || bpDia >= 110) ? "critical" : "warning",
                icon: "🩺",
                title: "Maternal Hypertension",
                detail: "BP " + (bpSys || "—") + "/" + (bpDia || "—") + " mmHg",
                action: (bpSys >= 160 || bpDia >= 110)
                    ? "URGENT: Severe hypertension. Antihypertensive + MgSO4 if indicated."
                    : "Monitor closely. Check for pre-eclampsia signs.",
            });
        }

        /* Maternal fever */
        const temp = parseFloat(latest.maternal_temperature);
        if (!isNaN(temp) && temp >= 38) {
            list.push({
                type: temp >= 39 ? "critical" : "warning",
                icon: "🌡️",
                title: "Maternal Fever",
                detail: "Temperature " + temp + "°C at " + latestHour + "h",
                action: "Investigate: chorioamnionitis, infection. Antibiotics if indicated.",
            });
        }

        return list;
    }, [entries]);

    /* ─── Partograph summary stats ─── */
    const summary = useMemo(function() {
        if (!entries.length) return null;
        const first = entries[0];
        const last = entries[entries.length - 1];
        const durationHours = ((parseFloat(last.hours_since_start) || 0) - (parseFloat(first.hours_since_start) || 0));
        return {
            totalEntries: entries.length,
            durationHours: durationHours.toFixed(1),
            latestDilation: last.cervical_dilation_cm || "—",
            latestDescent: last.descent_fifths != null ? last.descent_fifths + "/5" : "—",
            latestFHR: last.fetal_heart_rate || "—",
            latestBP: (last.maternal_bp_systolic ? last.maternal_bp_systolic + "/" + last.maternal_bp_diastolic : "—"),
            status: partograph ? partograph.status : "—",
        };
    }, [entries, partograph]);

    return {
        /* Constants */
        AMNIOTIC_FLUID,
        MOULDING_OPTIONS,
        URINE_PROTEIN_OPTIONS,
        URINE_ACETONE_OPTIONS,
        PARTOGRAPH_STATUS,
        /* Admission */
        admissionData,
        loadingAdmission,
        /* Partograph */
        partograph,
        entries,
        /* Entry form */
        entryForm,
        handleEntryChange,
        addEntry,
        editingEntry,
        startEditEntry,
        updateEntry,
        cancelEdit,
        /* Start form */
        startForm,
        handleStartChange,
        startPartograph,
        starting,
        /* Actions */
        completePartograph,
        computeHoursSinceStart,
        /* Chart data */
        dilationData,
        descentData,
        fhrData,
        contractionData,
        maternalData,
        alertLineData,
        actionLineData,
        maxHour,
        /* Alerts */
        alerts,
        /* Summary */
        summary,
        /* UI */
        activeTab,
        setActiveTab,
        saving,
    };
};
